// @ts-nocheck
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadCurrentAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadCurrentAccount";
import { getPageStateByType } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Icon from "@insite/mobius/Icon";
import Mail from "@insite/mobius/Icons/Mail";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import getColor from "@insite/mobius/utilities/getColor";
import Button from "@store/MobiusOverrides/Button/Button";
import { IconWrapper } from "@store/MobiusOverrides/Icon/Icon";
import setShowWelcomePopup from "@store/Framework/Store/Store/Components/WelcomePopup/Handlers/SetShowWelcomePopup";
import { getShowWelcomePopup } from "@store/Framework/Store/Store/Components/WelcomePopup/WelcomePopupSelector";
import WelcomeLetter from "@store/Library/Components/WelcomePackage/WelcomeLetter";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import getBooleanProperty from "@toolkit/Framework/Common/Utilities/getBooleanProperty";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { loadPageByType } from "@toolkit/Modules/client-framework/Store/Data/Pages/PagesActionCreators";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const welcomeLetterState = getPageStateByType(state, "WelcomeLetterPage");
    const userRoles = state.context.session.userRoles;
    const hasWelcomeUserRole = !!(userRoles && userRoles?.toLowerCase().indexOf("rsl_welcomepackage") !== -1);
    const hasReadWelcomeLetter = getBooleanProperty(state.context.session.properties?.hasReadWelcomeLetter);

    return {
        showModal: getShowWelcomePopup(state),
        welcomeLetter: welcomeLetterState.value,
        welcomeLetterIsLoading: welcomeLetterState.isLoading,
        hasWelcomeUserRole,
        hasReadWelcomeLetter,
        shouldLoadAccount: !getCurrentAccountState(state).value,
    };
};

const mapDispatchToProps = {
    loadCurrentAccount,
    setShowWelcomePopup,
    loadPageByType,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface WelcomePopupStyles {
    welcomePopupModal?: ModalPresentationProps;
}

export const welcomePopupStyles: WelcomePopupStyles = {
    welcomePopupModal: {
        cssOverrides: {
            modalBody: css`
                ${breakpoint("lg", "up")} {
                    max-width: ${x => x.theme.design.layout.contentWidth - 140}px;
                }
            `,
            modalTitle: css`
                background-color: ${getColor("primary.dark")};
                background-image: url("staticContent/images/blueGeometricBanner.png");
                background-size: cover;
                height: 72px;
                border: none;
            `,
            modalContent: css`
                background-color: ${getColor("secondary")};
                padding: 0;
                position: relative;
                min-height: ${pxToRem(200)};
            `,
        },
        closeButtonProps: {
            css: css`
                ${x => x.theme.modal.defaultProps?.closeButtonProps?.css};

                &:hover {
                    ${IconWrapper} {
                        background-color: ${getColor("primary.light")};
                    }
                }
                ${IconWrapper} {
                    color: ${getColor("primary.dark")};
                }
            `,
        },
        closeButtonIconProps: {
            size: 24,
            css: css`
                ${x => x.theme.modal.defaultProps?.closeButtonIconProps?.css};
                background-color: ${getColor("primary.main")};
                border-radius: 12px;
                padding: 4px;
            `,
        },
    },
};
const styles = welcomePopupStyles;

const WelcomePopup = ({
    showModal,
    shellContext,
    welcomeLetter,
    welcomeLetterIsLoading,
    hasWelcomeUserRole,
    hasReadWelcomeLetter,
    shouldLoadAccount,
    setShowWelcomePopup,
    loadPageByType,
    loadCurrentAccount,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(showModal);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
        setShowWelcomePopup(false);
    };

    const onOpenLetter = () => {
        // Used in CMS
        setModalIsOpen(true);
        setShowWelcomePopup(true);
    };

    const isWelcomeLetterContentLoaded = () => {
        return welcomeLetter !== undefined && welcomeLetter.id !== undefined;
    };

    useEffect(() => {
        if (shouldLoadAccount && hasWelcomeUserRole && !hasReadWelcomeLetter) {
            loadCurrentAccount();
        }
    }, [shouldLoadAccount, hasWelcomeUserRole, hasReadWelcomeLetter]);

    useEffect(() => {
        if (hasWelcomeUserRole && !hasReadWelcomeLetter) {
            setModalIsOpen(true);
            setShowWelcomePopup(true);
        }
    }, [hasWelcomeUserRole, hasReadWelcomeLetter]);

    useEffect(() => {
        if (modalIsOpen && showModal === false) {
            setModalIsOpen(false);
        }
    }, [showModal]);

    useEffect(() => {
        if (hasWelcomeUserRole && !hasReadWelcomeLetter && !welcomeLetterIsLoading && welcomeLetter === undefined) {
            loadPageByType("WelcomeLetterPage");
        }
    }, [hasWelcomeUserRole, hasReadWelcomeLetter, welcomeLetterIsLoading, welcomeLetter]);

    if (
        (!hasWelcomeUserRole || hasReadWelcomeLetter || welcomeLetterIsLoading || !isWelcomeLetterContentLoaded()) &&
        !shellContext.isInShell
    ) {
        return null;
    }

    return (
        <>
            {shellContext.isInShell && shellContext.isEditing && (
                <Button onClick={onOpenLetter}>
                    <Icon src={Mail}></Icon>
                </Button>
            )}
            <Modal
                {...styles.welcomePopupModal}
                isOpen={modalIsOpen && !welcomeLetterIsLoading && isWelcomeLetterContentLoaded()}
                handleClose={modalCloseHandler}
                sizeVariant="large"
            >
                <WelcomeLetter />
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(WelcomePopup)),
    definition: {
        group: "Basic",
        allowedContexts: ["Header"],
        displayName: "Welcome Letter Popup",
    },
};

export default widgetModule;
